import * as React from 'react';

import { faLoader } from '@awesome.me/kit-8a9525ab71/icons/classic/regular';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { cn } from 'src/lib/utils';

const buttonVariants = cva(
  `
    inline-flex shrink-0 cursor-pointer items-center justify-center
    whitespace-nowrap transition-all outline-none
    disabled:pointer-events-none
    [&_svg]:pointer-events-none [&_svg]:shrink-0
    disabled:[&_svg]:text-current
    [&_svg:not([class*='size-'])]:size-16
  `,
  {
    variants: {
      variant: {
        primary:
          `
            bg-baby-blue-darken-40 text-white shadow-button-primary
            hover:bg-baby-blue-darken-50/90
            focus-visible:bg-baby-blue-darken-30 focus-visible:ring-3
            focus-visible:ring-baby-blue-darken-30 focus-visible:ring-offset-2
            disabled:bg-baby-blue-lighten-90
            disabled:text-deep-blue-lighten-60 data-[selected=true]:bg-baby-blue-darken-70
          `,
        secondary:
          `
            border border-baby-blue-darken-10 bg-white text-baby-blue-darken-70
            shadow-button-outline
            hover:bg-white-darken-10
            focus-visible:bg-baby-blue-lighten-20
            focus-visible:text-silver-darken-90 focus-visible:ring-3
            focus-visible:ring-baby-blue focus-visible:ring-offset-2
            disabled:border-transparent
            disabled:bg-baby-blue-lighten-75
            disabled:text-deep-blue-lighten-60
            data-[selected=true]:bg-baby-blue-darken-35 data-[selected=true]:text-white
            [&_svg]:text-baby-blue-darken-25
          `,
        secondaryAlt:
          `
            border border-baby-blue-darken-15 bg-white text-baby-blue-darken-70
            shadow-button-outline
            hover:bg-baby-blue-lighten-90
            focus-visible:ring-3 focus-visible:ring-baby-blue
            focus-visible:ring-offset-2
            disabled:border-baby-blue-lighten-5
            disabled:text-deep-blue-lighten-60 data-[selected=true]:bg-baby-blue-lighten-60
          `,
        secondaryDestructive:
          `
            border border-feedback-red-darken-5 bg-white text-feedback-red-darken-15
            shadow-[#EB47474D]
            hover:bg-white-darken-10
            disabled:opacity-50
            [&_svg]:text-feedback-red-darken-15
          `,
        danger:
          `
            bg-red-darken-15 text-white shadow-button-primary
            hover:bg-red-darken-15/90
            focus-visible:bg-red-darken-5 focus-visible:ring-3
            focus-visible:ring-red-darken-15 focus-visible:ring-offset-2
            disabled:bg-red-lighten-90
            disabled:text-red-lighten-50 data-[selected=true]:bg-red-darken-40
          `,
        burgundy:
          `
            bg-burgundy text-white shadow-button-primary
            hover:bg-burgundy/90
            focus-visible:bg-burgundy-lighten-30 focus-visible:ring-3
            focus-visible:ring-burgundy focus-visible:ring-offset-2
            disabled:bg-burgundy-lighten-90
            disabled:text-burgundy-lighten-60 data-[selected=true]:bg-burgundy-darken-40
          `,
        success:
          `
            bg-green text-silver-darken-90 shadow-button-primary
            hover:bg-green-darken-15
            focus-visible:bg-green-lighten-15 focus-visible:ring-3
            focus-visible:ring-green-darken-10 focus-visible:ring-offset-2
            disabled:bg-green-lighten-90
            disabled:text-deep-blue-lighten-60
            data-[selected=true]:bg-green-darken-40 data-[selected=true]:text-white
          `,
        neutral:
          `
            bg-silver-darken-20 text-silver-darken-90 shadow-button-primary
            hover:bg-silver-darken-30
            focus-visible:ring-3 focus-visible:ring-silver-darken-50
            focus-visible:ring-offset-2
            disabled:bg-silver
            disabled:text-silver-darken-40 data-[selected=true]:bg-silver-darken-40
          `,
        link: `
          underline-offset-4
          hover:underline
          disabled:opacity-50
        `,
        ghost:
          `
            border-none bg-transparent text-baby-blue-darken-40
            hover:bg-baby-blue-lighten-75
            focus-visible:ring-2 focus-visible:ring-baby-blue
            disabled:text-baby-blue-lighten-40
            data-[selected=true]:bg-baby-blue-lighten-60
          `,
        clear: 'disabled:opacity-50',
      },
      size: {
        sm: 'gap-4 px-8 py-4 text-small-brand-medium',
        md: 'gap-6 px-12 py-8 text-body-brand-medium',
        lg: 'gap-8 px-16 py-12 text-body-brand-medium',
      },
      radius: {
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
      radius: 'sm',
    },
  },
);

interface ButtonProps
  extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  icon?: IconProp;
  selected?: boolean;
}

function Button({
  className,
  variant,
  size,
  radius,
  icon,
  disabled,
  children,
  asChild = false,
  loading = false,
  selected,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';

  const displayIcon = loading ? faLoader : icon;

  return (
    <Comp
      aria-pressed={selected}
      className={cn(buttonVariants({ variant, radius, size, className }))}
      data-selected={selected}
      data-slot="button"
      disabled={disabled || (!asChild && loading)}
      type="button"
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {displayIcon && <FontAwesomeIcon icon={displayIcon} spin={loading} />}

          {children}
        </>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
